import {
  collection,
  query,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  Timestamp,
  DocumentData,
  limit as firestoreLimit,
  orderBy,
  startAfter,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import { COLLECTIONS } from "@/config/constants";
import { User, SubscriptionTier } from "@/types";

/**
 * Users Service
 * Handles all Firestore operations related to users (Read-Only for Admin)
 */
export const usersService = {
  /**
   * Get users with optional server-side filters and cursor-based pagination
   */
  getUsers: async (filters?: {
    role?: string;
    isOwner?: boolean;
    subscriptionTier?: SubscriptionTier;
    limit?: number;
    startAfterTimestamp?: Timestamp;
  }): Promise<User[]> => {
    try {
      const usersRef = collection(db, COLLECTIONS.USERS);
      const constraints: Parameters<typeof query>[1][] = [];

      // Server-side filters
      // Note: isOwner === false can NOT be server-side because documents without
      // the "owner" field at all won't match `where("owner.isOwner", "==", false)`.
      // We only filter server-side for true; false is handled client-side after load.
      if (filters?.isOwner === true) {
        constraints.push(where("owner.isOwner", "==", true));
      }
      if (filters?.role) {
        constraints.push(where("role", "==", filters.role));
      }

      constraints.push(orderBy("createdAt", "desc"));

      if (filters?.startAfterTimestamp) {
        constraints.push(startAfter(filters.startAfterTimestamp));
      }

      const pageSize = Math.min(filters?.limit ?? 100, 200);
      constraints.push(firestoreLimit(pageSize));

      const snapshot = await getDocs(query(usersRef, ...constraints));
      let users = snapshot.docs.map((doc: DocumentData) => ({
        id: doc.id,
        ...doc.data(),
      })) as User[];

      // Subscription tier kann nicht einfach server-seitig gefiltert werden
      // (verschachtelte Felder mit OR-Logik), daher client-seitig
      if (filters?.subscriptionTier) {
        users = users.filter(
          (user) => user.subscription?.tier === filters.subscriptionTier,
        );
      }

      return users;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw new Error("Fehler beim Laden der Nutzer");
    }
  },

  /**
   * Search users by name or email (client-side scoring on limited result set)
   */
  searchUsers: async (searchTerm: string, limitCount = 10): Promise<User[]> => {
    try {
      const term = searchTerm.toLowerCase().trim();
      if (!term) return [];

      const usersRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(
        query(usersRef, orderBy("createdAt", "desc"), firestoreLimit(200)),
      );

      return snapshot.docs
        .map((doc: DocumentData) => ({ id: doc.id, ...doc.data() } as User))
        .filter(
          (u) =>
            u.email?.toLowerCase().includes(term) ||
            u.profile?.displayName?.toLowerCase().includes(term),
        )
        .slice(0, limitCount);
    } catch (error) {
      console.error("Error searching users:", error);
      return [];
    }
  },

  /**
   * Get single user by ID
   */
  getUser: async (userId: string): Promise<User | null> => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return null;
      }

      return {
        id: userDoc.id,
        ...userDoc.data(),
      } as User;
    } catch (error) {
      console.error("Error fetching user:", error);
      throw new Error("Fehler beim Laden des Nutzers");
    }
  },

  /**
   * Get users statistics using server-side aggregation to avoid loading all documents
   */
  getUsersStats: async (): Promise<{
    total: number;
    owners: number;
    activeSubscriptions: number;
    trialUsers: number;
  }> => {
    try {
      const usersRef = collection(db, COLLECTIONS.USERS);

      const [totalSnap, ownersSnap] = await Promise.all([
        getCountFromServer(query(usersRef)),
        getCountFromServer(query(usersRef, where("owner.isOwner", "==", true))),
      ]);

      return {
        total: totalSnap.data().count,
        owners: ownersSnap.data().count,
        activeSubscriptions: 0,
        trialUsers: 0,
      };
    } catch (error) {
      console.error("Error fetching users stats:", error);
      throw new Error("Fehler beim Laden der Nutzer-Statistiken");
    }
  },

  /**
   * Get all owners — server-side filter statt Full-Scan
   */
  getOwners: async (): Promise<User[]> => {
    try {
      const usersRef = collection(db, COLLECTIONS.USERS);
      const snapshot = await getDocs(
        query(usersRef, where("owner.isOwner", "==", true)),
      );
      return snapshot.docs.map((d: DocumentData) => ({
        id: d.id,
        ...d.data(),
      })) as User[];
    } catch (error) {
      console.error("Error fetching owners:", error);
      throw new Error("Fehler beim Laden der Owner");
    }
  },

  /**
   * Update trial period for an owner.
   * Schreibt BEIDE Strukturen:
   * - Root-level (NEW, wird vom Owner-Dashboard als Priorität gelesen)
   * - Nested subscription (OLD, für Abwärtskompatibilität)
   *
   * @param userId       - UID des Owners
   * @param trialEndDate - Neues Trial-Enddatum
   * @param extendTrial  - ob isTrialActive = true gesetzt werden soll
   */
  updateTrialPeriod: async (
    userId: string,
    trialEndDate: Date,
    extendTrial: boolean,
  ): Promise<void> => {
    try {
      const userRef = doc(db, COLLECTIONS.USERS, userId);
      const trialEndTimestamp = Timestamp.fromDate(trialEndDate);
      await updateDoc(userRef, {
        // Root-level (NEW structure — Owner Dashboard liest dies mit Priorität)
        hasActiveSubscription: extendTrial,
        subscriptionType: extendTrial ? "trial" : "free",
        subscriptionEndDate: trialEndTimestamp,

        // Nested subscription (OLD structure — Fallback)
        "subscription.isTrialActive": extendTrial,
        "subscription.trialEndDate": trialEndTimestamp,
        "subscription.tier": extendTrial ? "trial" : "free",

        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error("Error updating trial period:", error);
      throw new Error("Fehler beim Aktualisieren des Free Trials");
    }
  },
};
